/**
 * The "one active turn" lifecycle. Each factory turn registers its controller here (see `withActiveTurn`), so a
 * turn is observable and stoppable whoever started it — composer or programmatic. Nav-away/unmount aborts it.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import type { RefObject } from "react"
import { log } from "helpers/logger"
import { isExpectedStreamGoneError } from "./chat-api-errors"
import type { StreamingChatTransport } from "./streaming-transport"

export interface ActiveTurnController {
  /** A turn (composer OR programmatic) is streaming. */
  isTurnActive: boolean
  /** Register a fresh controller as the active turn; its `signal` is what the transport aborts on. */
  beginTurn: () => AbortController
  /** Clear the active turn once its stream settles (only if still the registered one). */
  endTurn: (controller: AbortController) => void
  /** Abort the active turn's client read. Does NOT stop the server — that is `stopServerStream`. */
  stopTurn: () => void
  /** Server-side stop: DELETE the thread's active stream so op-summarize halts. */
  stopServerStream: () => void
  /** Captured per-turn by the adapter, so a stopped turn's parked chunks can't hijack a fresh view. */
  getTurnSignal: () => AbortSignal | undefined
  /** The registered controller, read fresh — the programmatic send rejects a concurrent turn, then captures its own across awaits. */
  getActiveTurn: () => AbortController | null
}

/** `activeThreadIdRef` is a param because the server-side stop reads the LIVE thread id, not a send-time closure. */
export function useActiveTurn(
  streamingTransport: StreamingChatTransport,
  activeThreadIdRef: RefObject<string | null>,
): ActiveTurnController {
  const [isTurnActive, setIsTurnActive] = useState(false)
  const activeTurnRef = useRef<AbortController | null>(null)

  const beginTurn = useCallback(() => {
    const controller = new AbortController()
    activeTurnRef.current = controller
    setIsTurnActive(true)
    return controller
  }, [])
  const endTurn = useCallback((controller: AbortController) => {
    if (activeTurnRef.current !== controller) return
    activeTurnRef.current = null
    setIsTurnActive(false)
  }, [])

  // A client abort leaves the server generating, so attach-on-select could reattach a "stopped" turn — DELETE the
  // stream too. Optional-chained: the shared transport has no cancelStream yet, so this no-ops until it does.
  const stopServerStream = useCallback(() => {
    streamingTransport.cancelStream?.(activeThreadIdRef.current)?.catch((error: unknown) => {
      if (!isExpectedStreamGoneError(error)) log(error)
    })
  }, [streamingTransport, activeThreadIdRef])

  // Detach only: the server stream survives so attach-on-select can resume it. Killing it is Stop's job.
  const stopTurn = useCallback(() => {
    activeTurnRef.current?.abort()
  }, [])
  useEffect(() => () => activeTurnRef.current?.abort(), [])

  const getTurnSignal = useCallback(() => activeTurnRef.current?.signal, [])
  const getActiveTurn = useCallback(() => activeTurnRef.current, [])

  return { isTurnActive, beginTurn, endTurn, stopTurn, stopServerStream, getTurnSignal, getActiveTurn }
}
